"use client"

import { useState, type ReactNode } from "react"

interface ImageGalleryProps {
  /** Array of image URLs */
  images: string[]
  /** Alt text base for images */
  alt: string
  /** Optional badge element to overlay on main image (e.g. Energy Star) */
  badge?: ReactNode
  /** Additional classes on the wrapper */
  className?: string
}

export function ImageGallery({ images, alt, badge, className = "" }: ImageGalleryProps) {
  const [selectedImage, setSelectedImage] = useState(0)

  return (
    <div className={`flex gap-3 ${className}`}>
      {/* Vertical Thumbnails (desktop) */}
      <div className="hidden lg:flex flex-col gap-2 w-[72px] flex-shrink-0">
        {images.map((img, i) => (
          <button
            key={i}
            onClick={() => setSelectedImage(i)}
            className={`aspect-square rounded-lg overflow-hidden bg-white border-2 transition-all cursor-pointer ${
              i === selectedImage
                ? "border-[#ff9951] ring-2 ring-[#ff9951]/20"
                : "border-gray-200 hover:border-[#ff9951]/50"
            }`}
          >
            <img
              className="w-full h-full object-contain p-1.5"
              src={img}
              alt={`${alt} - View ${i + 1}`}
            />
          </button>
        ))}
      </div>

      {/* Main Image */}
      <div className="flex-1">
        <div className="relative aspect-square rounded-lg overflow-hidden bg-white border border-gray-200 group">
          <img
            className="w-full h-full object-contain p-6 transition-transform duration-700 group-hover:scale-105"
            src={images[selectedImage]}
            alt={alt}
          />
          {badge && (
            <div className="absolute top-4 left-4">{badge}</div>
          )}
        </div>

        {/* Mobile Thumbnails (horizontal) */}
        <div className="grid grid-cols-4 gap-2 mt-3 lg:hidden">
          {images.map((img, i) => (
            <button
              key={i}
              onClick={() => setSelectedImage(i)}
              className={`aspect-square rounded-lg overflow-hidden bg-gray-50 border-2 transition-all cursor-pointer ${
                i === selectedImage
                  ? "border-[#ff9951] ring-2 ring-[#ff9951]/20"
                  : "border-gray-200 hover:border-[#ff9951]/50"
              }`}
            >
              <img
                className="w-full h-full object-contain p-1.5"
                src={img}
                alt={`${alt} - View ${i + 1}`}
              />
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
